import CommonText from "@/components/text/CommonText";
import React, { FC, HTMLAttributes, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

interface CustomDivProps extends HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  setCloseModal: (value: boolean) => void;
  notShowCrossBtn?: boolean;
  headingText?: string;
  contentClassName?: string;
}

const ModalLayout: FC<CustomDivProps> = ({
  children,
  setCloseModal,
  notShowCrossBtn,
  headingText,
  contentClassName,
  ...props
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Prevent body scrolling when modal is open and handle keyboard events
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');
    
    // Handle Escape key to close modal
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setCloseModal(false);
      }
    };
    
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.body.style.overflow = originalStyle;
      document.body.classList.remove('modal-open');
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [setCloseModal]);
  const baseClassesForMain = "modal-overlay";

  const modalContentClasses = contentClassName || "modal-content w-[900px] h-fit max-h-[80vh] overflow-y-auto";
 

  const handleOutsideClick = (event: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setCloseModal(false);
    }
  };

  const modalContent = (
    <div className={baseClassesForMain} {...props} onClick={handleOutsideClick}>
      <div className={modalContentClasses} ref={modalRef}>
        {!notShowCrossBtn && (
          <button
            onClick={() => {
              setCloseModal(false);
            }}
            type="button"
            className="absolute top-4 right-4 z-[10001] w-8 h-8 bg-white hover:bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:text-gray-800 shadow-lg transition-colors duration-200"
            aria-label="Close modal"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        {headingText && (
          <CommonText size="base" className="text-[#f05a24] px-4 mt-4 font-bold">
            {headingText}
          </CommonText>
        )}
        {children}
      </div>
    </div>
  );

  // Use portal to render modal at document body level
  if (typeof document !== "undefined") {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
};

export default ModalLayout;
